/**
 * ImplantHealthCard — готовност за имплант и поддръжка на поставен
 * имплант, изчислена в backend от реалните данни на пациента
 * (dental_intelligence_engine.compute_implant_health).
 */
import React, { useState } from "react";
import axios from "axios";
import { Anchor, AlertTriangle, ShieldCheck, Wrench, CalendarClock, Loader2 } from "lucide-react";
// iter1111 — delegate to the factor-summary card when backend retired the composite.
import { ImplantReadinessFactorCard } from "./DhiRetiredCards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND = {
  green: "#10B981", yellow: "#F59E0B", orange: "#F97316", red: "#EF4444",
};

export default function ImplantHealthCard({ data, isDark = false, cardCls = "", onSaved, lang = "bg" }) {
  const st = data?.implants || {};
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    has_implant: !!st.has_implant,
    planning: !!st.planning,
    count: st.count || 1,
    placed_year: st.placed_year || "",
  });

  // iter1111 — If the backend returned the retired/clinician_checklist
  // shape (no numeric score), render the ITI consensus checklist via the
  // new factor-summary primitive instead of returning null (which hid
  // the whole card from view and gave the user no clinical context).
  if (data && (data._retired || data.status === "clinician_checklist"
               || data.status === "retired_unvalidated")) {
    return <ImplantReadinessFactorCard data={data} lang={lang} isDark={isDark} />;
  }
  if (!data || data.score === undefined) return null;
  const border = isDark ? "border-white/10" : "border-gray-200";
  const color = BAND[data.band] || "#0EA5E9";
  const maintenance = data.mode === "maintenance";

  const save = async (payload) => {
    setSaving(true);
    try {
      await axios.post(`${API}/dental-prevention/implants`, payload);
      setOpen(false);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-implant-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Anchor className="w-4 h-4 text-cyan-500" /> Здраве на импланта
          </div>
          <p className="text-[11px] opacity-70 mt-0.5">
            Средата, в която имплантът трябва да се задържи — от Вашите данни.
          </p>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-600 dark:text-cyan-300"
              data-testid="implant-mode">{data.mode_label_bg}</span>
            {st.summary_bg && (
              <span className="text-[10px] opacity-70" data-testid="implant-status-summary">
                {st.summary_bg}
              </span>
            )}
            <button type="button" onClick={() => setOpen((v) => !v)}
              className="text-[10px] underline opacity-70 hover:opacity-100"
              data-testid="implant-declare-toggle">
              {st.declared ? "Промени данните за импланта" : "Имам или планирам имплант"}
            </button>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-3xl font-extrabold tabular-nums" style={{ color }}
            data-testid="implant-score">{data.score}</div>
          <div className="text-[10px] font-bold" style={{ color }}
            data-testid="implant-readiness">{data.readiness_bg}</div>
        </div>
      </div>

      <div className="mt-2 h-2 rounded-full overflow-hidden"
        style={{ background: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)" }}>
        <div className="h-full rounded-full transition-all"
          style={{ width: `${data.score}%`, background: color }} />
      </div>

      {open && (
        <div className={`mt-3 p-3 rounded-xl border ${border}`} data-testid="implant-declare-form">
          <div className="flex flex-wrap gap-2 mb-2">
            {[
              { key: "has", label: "Имам имплант" },
              { key: "plan", label: "Планирам имплант" },
              { key: "none", label: "Нямам и не планирам" },
            ].map((o) => {
              const active = (o.key === "has" && form.has_implant)
                || (o.key === "plan" && !form.has_implant && form.planning)
                || (o.key === "none" && !form.has_implant && !form.planning);
              return (
                <button key={o.key} type="button"
                  onClick={() => setForm((f) => ({
                    ...f,
                    has_implant: o.key === "has",
                    planning: o.key === "plan",
                  }))}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${
                    active ? "bg-cyan-600 text-white border-cyan-600" : border}`}
                  data-testid={`implant-opt-${o.key}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
          {form.has_implant && (
            <div className="flex flex-wrap gap-2 items-end">
              <label className="text-[11px] opacity-75">
                Брой
                <input type="number" min="1" max="32" value={form.count}
                  onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
                  className={`mt-0.5 w-20 px-2 py-1 rounded-lg border bg-transparent text-sm ${border}`}
                  data-testid="implant-count-input" />
              </label>
              <label className="text-[11px] opacity-75">
                Година на поставяне
                <input type="number" min="1950" max={new Date().getFullYear()}
                  value={form.placed_year} placeholder="2021"
                  onChange={(e) => setForm((f) => ({ ...f, placed_year: e.target.value }))}
                  className={`mt-0.5 w-28 px-2 py-1 rounded-lg border bg-transparent text-sm ${border}`}
                  data-testid="implant-year-input" />
              </label>
            </div>
          )}
          <button type="button" disabled={saving}
            onClick={() => save({
              has_implant: form.has_implant,
              planning: form.planning,
              count: Number(form.count) || 1,
              placed_year: form.placed_year ? Number(form.placed_year) : null,
            })}
            className="mt-3 px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="implant-save-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Запиши
          </button>
        </div>
      )}

      {data.blockers?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="implant-blockers">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
            {maintenance ? "Най-силните заплахи за импланта" : "Спирачки — решават се преди хирургията"}
          </div>
          {data.blockers.map((b, i) => (
            <div key={i} className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/30">
              <div className="text-[11px] font-bold text-rose-500 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {b.label_bg}
              </div>
              <div className="text-[10px] opacity-85 mt-0.5">{b.why_bg}</div>
            </div>
          ))}
        </div>
      )}

      {data.risks?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="implant-risks">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
            Фактори за внимание
          </div>
          {data.risks.map((r, i) => (
            <div key={i} className={`p-2 rounded-lg border ${border}`}>
              <div className="text-[11px] font-bold">{r.label_bg}</div>
              <div className="text-[10px] opacity-75 mt-0.5">{r.why_bg}</div>
            </div>
          ))}
        </div>
      )}

      {data.favorable?.length > 0 && (
        <div className="mt-3" data-testid="implant-favorable">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1">
            В своя полза
          </div>
          <ul className="space-y-0.5">
            {data.favorable.map((f, i) => (
              <li key={i} className="text-[10px] text-emerald-600 dark:text-emerald-400 inline-flex items-start gap-1">
                <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
      )}

      {maintenance && data.recall_months && (
        <div className="mt-3 p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/25"
          data-testid="implant-recall">
          <div className="text-[11px] font-bold inline-flex items-center gap-1.5 text-cyan-700 dark:text-cyan-300">
            <CalendarClock className="w-3.5 h-3.5" />
            Вашият интервал за контрола: на {data.recall_months} месеца
          </div>
          {data.recall_why_bg?.length > 0 && (
            <div className="text-[10px] opacity-80 mt-0.5">
              По-често заради: {data.recall_why_bg.join(", ")}.
            </div>
          )}
        </div>
      )}

      <div className="mt-3" data-testid="implant-actions">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1">
          Следваща стъпка
        </div>
        <ul className="space-y-0.5">
          {data.actions_bg.map((a, i) => (
            <li key={i} className="text-[11px] opacity-85">· {a}</li>
          ))}
        </ul>
      </div>

      <div className="mt-3" data-testid="implant-maintenance">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1 inline-flex items-center gap-1">
          <Wrench className="w-3 h-3" /> Поддръжка на поставен имплант
        </div>
        <ul className="space-y-0.5">
          {data.maintenance_bg.map((m, i) => (
            <li key={i} className="text-[10px] opacity-75">· {m}</li>
          ))}
        </ul>
      </div>

      <div className="text-[9px] opacity-50 mt-2">{data.note_bg}</div>
    </div>
  );
}
